import { Component, Input, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, AfterViewInit } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { FormFieldAbstractComponent } from '../abstract.component';
import { provideTranslation } from '@skyeng/libs/shared/i18n';
import { FormFieldsService } from '@shared/shared';
import { TranslocoService } from '@ngneat/transloco';
import { DateTimeAdapter } from '@danielmoncada/angular-datetime-picker';
import { SkyUiPopoverDirective } from '@skyeng/libs/shared/ui-kit';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { Platform } from '@angular/cdk/platform';
import { CUSTOM_DATE_FORMATS, DatepickerFieldUtcAdapter } from './datepicker-field-utc-adapter';
import { CalendarService } from '@wfm/shared';
import { PersonPageService } from '@crm/persons/person/services/person-page.service';
import { takeUntil } from 'rxjs/operators';
import { UntypedFormControl } from '@angular/forms';

const MskTimezone = 'Europe/Moscow';

@Component({
  selector: 'crm-local-date-picker',
  templateUrl: './local-date-picker.component.html',
  styleUrls: ['./local-date-picker.component.scss'],
  viewProviders: [provideTranslation('localDatePicker', () => import.meta.webpackContext!('./translation'))],
  providers: [
    { provide: DateAdapter, useClass: DatepickerFieldUtcAdapter, deps: [MAT_DATE_LOCALE, Platform] },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LocalDatePickerComponent extends FormFieldAbstractComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() timezone: string = MskTimezone;
  @ViewChild('popover') popover: SkyUiPopoverDirective;

  fakeControlDate = new UntypedFormControl();

  value = new Date().toISOString();

  private readonly lang = this.transloco.getActiveLang() || this.transloco.getDefaultLang();
  private destroy$: Subject<void> = new Subject<void>();

  mskTimezone = MskTimezone;

  dateTimeMin: Date;
  dateTimeMax: Date;
  startAt: Date;
  selectedDate = new Date();

  get readonlyValue$(): Observable<any> {
    return of(this.getFormControl().value);
  }

  constructor(
    protected formFieldsService: FormFieldsService,
    private transloco: TranslocoService,
    private adapter: DateTimeAdapter<Date>,
    private personPage: PersonPageService,
    private calendarService: CalendarService,
  ) {
    super(formFieldsService);
    this.adapter.setLocale(this.lang);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.setDateRestrictions();
    this.personPage
      .getPerson()
      .pipe(takeUntil(this.destroy$))
      .subscribe(person => {
        this.timezone = person.timezone;
      });
  }

  ngAfterViewInit(): void {
    this.value = this.calendarService.userDateTime(new Date(), MskTimezone).toISOString();
    this.getFormControl()
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.fakeControlDate.setValue(this.value);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setDateRestrictions(): void {
    const minDate = this.formFieldsService.getValidatorConstraint(this.field, 'min');
    if (minDate) {
      this.dateTimeMin = minDate;
    }

    const maxDate = this.formFieldsService.getValidatorConstraint(this.field, 'max');
    if (maxDate) {
      this.dateTimeMax = maxDate;
    }
  }

  close(): void {
    this.popover.hide();
  }

  setDate(date): void {
    this.popover.hide();
    this.value = date;
    // todo fix: transfer to moscow timezone due backend always reduces value -3 hour
    this.getFormControl().setValue(this.calendarService.toSerializeValue(date));
  }
}
